import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  BookOpen, Save, X, Image, Plus, Trash2, MapPin, Phone, Mail, 
  Globe, Clock, Users, CheckCircle, Upload, Loader2, Crown, Calendar,
  ArrowLeft, DollarSign, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';

export default function WorkshopEditor({ workshop, onSave, onCancel }) {
  // Form state
  var [formData, setFormData] = useState({
    // Basic info
    name: '',
    type: '',
    description: '',
    target_audience: '',
    
    // Contact & Location
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    address: '',
    city: '',
    region: '',
    facebook: '',
    instagram: '',
    
    // Pricing
    price: '',
    price_description: '',
    
    // Plan
    plan_type: 'gratuito',
    plan_level: 0,
    premium_until: '',
    
    // Images
    logo: '',
    gallery: [],
    
    // Details
    modality: 'presencial',
    duration: '',
    schedule: '',
    capacity: '',
    requirements: '',
    
    // Status
    is_public: true,
    is_verified: false
  });
  
  var [saving, setSaving] = useState(false);
  var [uploading, setUploading] = useState(false);
  var [activeSection, setActiveSection] = useState('info');

  // Workshop types
  var workshopTypes = [
    'Prevención', 'Tratamiento', 'Rehabilitación', 'Reinserción',
    'Apoyo Familiar', 'Grupos de Ayuda', 'Capacitación', 'Charla',
    'Seminario', 'Retiro', 'Terapia Grupal', 'Arte-Terapia', 'Otro'
  ];

  // Modality options
  var modalityOptions = [
    { value: 'presencial', label: 'Presencial' },
    { value: 'online', label: 'Online' },
    { value: 'hibrido', label: 'Híbrido' }
  ];

  // Plan options
  var planOptions = [
    { value: 'gratuito', label: 'Gratuito', level: 0, color: 'bg-slate-600' },
    { value: 'destacado', label: 'Destacado', level: 1, color: 'bg-blue-600' },
    { value: 'premium', label: 'Premium', level: 2, color: 'bg-amber-600' },
    { value: 'elite', label: 'Elite', level: 3, color: 'bg-purple-600' }
  ];

  var sections = [
    { id: 'info', label: 'Información Básica', icon: BookOpen },
    { id: 'contact', label: 'Contacto y Ubicación', icon: Phone },
    { id: 'pricing', label: 'Precios', icon: DollarSign },
    { id: 'plan', label: 'Plan y Suscripción', icon: Crown },
    { id: 'images', label: 'Galería de Imágenes', icon: Image },
    { id: 'details', label: 'Detalles del Taller', icon: Clock },
    { id: 'status', label: 'Estado', icon: CheckCircle }
  ];

  useEffect(function() {
    if (workshop) {
      setFormData({
        name: workshop.name || '',
        type: workshop.type || '',
        description: workshop.description || '',
        target_audience: workshop.target_audience || '',
        phone: workshop.phone || '',
        whatsapp: workshop.whatsapp || '',
        email: workshop.email || '',
        website: workshop.website || '',
        address: workshop.address || '',
        city: workshop.city || '',
        region: workshop.region || '',
        facebook: workshop.facebook || '',
        instagram: workshop.instagram || '',
        price: workshop.price || '',
        price_description: workshop.price_description || '',
        plan_type: workshop.plan_type || 'gratuito',
        plan_level: workshop.plan_level || 0,
        premium_until: workshop.premium_until ? workshop.premium_until.split('T')[0] : '',
        logo: workshop.logo || '',
        gallery: workshop.gallery || [],
        modality: workshop.modality || 'presencial',
        duration: workshop.duration || '',
        schedule: workshop.schedule || '',
        capacity: workshop.capacity || '',
        requirements: workshop.requirements || '',
        is_public: workshop.is_public !== false,
        is_verified: workshop.is_verified || false
      });
    }
  }, [workshop]);

  function updateField(field, value) {
    setFormData(function(prev) {
      var updated = Object.assign({}, prev);
      updated[field] = value;
      return updated;
    });
  }

  async function handleImageUpload(e, isLogo) {
    var file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no debe superar 10MB');
      return;
    }
    
    // Check file type
    var allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    var allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
    var ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext || '')) {
      toast.error('Formato no soportado. Use JPG, PNG, WebP, GIF o HEIC');
      return;
    }

    setUploading(true);
    var formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      var res = await authAxios.post(API + '/upload/image', formDataUpload, {
        withCredentials: true
      });
      
      if (isLogo) {
        updateField('logo', res.data.url);
      } else {
        setFormData(function(prev) {
          return Object.assign({}, prev, { gallery: prev.gallery.concat([res.data.url]) });
        });
      }
      toast.success('Imagen subida correctamente');
    } catch (err) {
      var errorMsg = err.response?.data?.detail || 'Error al subir imagen';
      toast.error(errorMsg);
    }
    setUploading(false);
  }

  function removeGalleryImage(index) {
    setFormData(function(prev) {
      var newGallery = prev.gallery.filter(function(_, i) { return i !== index; });
      return Object.assign({}, prev, { gallery: newGallery });
    });
  }

  function setMainImage(index) {
    setFormData(function(prev) {
      var newGallery = prev.gallery.slice();
      var mainImg = newGallery.splice(index, 1)[0];
      newGallery.unshift(mainImg);
      return Object.assign({}, prev, { 
        gallery: newGallery,
        logo: mainImg 
      });
    });
    toast.success('Imagen principal actualizada');
  }

  async function handleSave() {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      var dataToSend = Object.assign({}, formData);
      
      // Convert empty strings to null for optional fields
      if (!dataToSend.premium_until) dataToSend.premium_until = null;
      if (!dataToSend.price) dataToSend.price = null;
      if (!dataToSend.capacity) dataToSend.capacity = null;

      await authAxios.put(
        API + '/admin/workshops/' + workshop.workshop_id,
        dataToSend,
        { withCredentials: true }
      );
      toast.success('Taller actualizado exitosamente');
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onCancel} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {workshop ? 'Editar Taller' : 'Nuevo Taller'}
            </h1>
            <p className="text-slate-400">{formData.name || 'Sin nombre'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="border-slate-700 text-slate-300">
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Cambios
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar Navigation */}
        <div className="col-span-3">
          <Card className="bg-slate-900 border-slate-800 sticky top-6">
            <CardContent className="p-2">
              {sections.map(function(section) {
                var Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={function() { setActiveSection(section.id); }}
                    className={'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ' +
                      (activeSection === section.id 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white')}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{section.label}</span>
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="col-span-9 space-y-6">
          {/* Información Básica */}
          {activeSection === 'info' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-emerald-400" />
                  Información Básica
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nombre del Taller *</Label>
                    <Input 
                      value={formData.name}
                      onChange={function(e) { updateField('name', e.target.value); }}
                      placeholder="Taller de Prevención de Recaídas"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Tipo de Taller</Label>
                    <select
                      value={formData.type}
                      onChange={function(e) { updateField('type', e.target.value); }}
                      className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                    >
                      <option value="">Seleccionar...</option>
                      {workshopTypes.map(function(type) {
                        return <option key={type} value={type}>{type}</option>;
                      })}
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Descripción</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={function(e) { updateField('description', e.target.value); }}
                    placeholder="Describe el contenido, objetivos y metodología del taller..."
                    className="bg-slate-800 border-slate-700 text-white min-h-[120px]"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Público Objetivo</Label>
                  <Input 
                    value={formData.target_audience}
                    onChange={function(e) { updateField('target_audience', e.target.value); }}
                    placeholder="Adultos en recuperación, familiares, profesionales de la salud..."
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contacto y Ubicación */}
          {activeSection === 'contact' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Phone className="w-5 h-5 text-emerald-400" />
                  Contacto y Ubicación
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Teléfono</Label>
                    <Input 
                      value={formData.phone}
                      onChange={function(e) { updateField('phone', e.target.value); }}
                      placeholder="+56 9 1234 5678"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">WhatsApp</Label>
                    <Input 
                      value={formData.whatsapp}
                      onChange={function(e) { updateField('whatsapp', e.target.value); }}
                      placeholder="+56 9 1234 5678"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={function(e) { updateField('email', e.target.value); }}
                      placeholder="contacto@taller.cl"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Sitio Web</Label>
                    <Input 
                      value={formData.website}
                      onChange={function(e) { updateField('website', e.target.value); }}
                      placeholder="https://www.mitaller.cl"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Dirección</Label>
                  <Input 
                    value={formData.address}
                    onChange={function(e) { updateField('address', e.target.value); }}
                    placeholder="Av. Providencia 1234, Oficina 567"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Ciudad</Label>
                    <Input 
                      value={formData.city}
                      onChange={function(e) { updateField('city', e.target.value); }}
                      placeholder="Santiago"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Región</Label>
                    <Input 
                      value={formData.region}
                      onChange={function(e) { updateField('region', e.target.value); }}
                      placeholder="Metropolitana"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-700">
                  <Label className="text-slate-300 mb-3 block">Redes Sociales</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-xs">Facebook</Label>
                      <Input 
                        value={formData.facebook}
                        onChange={function(e) { updateField('facebook', e.target.value); }}
                        placeholder="facebook.com/..."
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Instagram</Label>
                      <Input 
                        value={formData.instagram}
                        onChange={function(e) { updateField('instagram', e.target.value); }}
                        placeholder="@usuario"
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Precios */}
          {activeSection === 'pricing' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                  Precios
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Precio (CLP)</Label>
                    <Input 
                      type="number"
                      value={formData.price}
                      onChange={function(e) { updateField('price', e.target.value ? parseInt(e.target.value) : ''); }}
                      placeholder="Ej: 50000"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Dejar vacío si es gratuito</p>
                  </div>
                  <div>
                    <Label className="text-slate-300">Descripción del Precio</Label>
                    <Input 
                      value={formData.price_description}
                      onChange={function(e) { updateField('price_description', e.target.value); }}
                      placeholder="Por sesión, mensual, curso completo..."
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>
                
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h4 className="text-white font-medium mb-2">Vista Previa</h4>
                  <p className="text-slate-300">
                    {formData.price ? (
                      <span className="text-emerald-400 font-semibold">
                        ${Number(formData.price).toLocaleString('es-CL')} {formData.price_description && '(' + formData.price_description + ')'}
                      </span>
                    ) : (
                      <span className="text-emerald-400 font-semibold">Gratuito</span>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Plan y Suscripción */}
          {activeSection === 'plan' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  Plan y Suscripción
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-slate-300 mb-3 block">Tipo de Plan</Label>
                  <div className="grid grid-cols-4 gap-3">
                    {planOptions.map(function(plan) {
                      return (
                        <button
                          key={plan.value}
                          type="button"
                          onClick={function() { 
                            updateField('plan_type', plan.value);
                            updateField('plan_level', plan.level);
                          }}
                          className={'p-4 rounded-lg border-2 transition-all ' +
                            (formData.plan_type === plan.value 
                              ? 'border-emerald-500 bg-emerald-500/10' 
                              : 'border-slate-700 hover:border-slate-600')}
                        >
                          <div className={'w-full h-2 rounded mb-2 ' + plan.color}></div>
                          <p className="text-white font-medium">{plan.label}</p>
                          <p className="text-xs text-slate-400">Nivel {plan.level}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nivel de Prioridad</Label>
                    <Input 
                      type="number"
                      min="0"
                      max="10"
                      value={formData.plan_level}
                      onChange={function(e) { updateField('plan_level', parseInt(e.target.value) || 0); }}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Mayor nivel = aparece primero en listados</p>
                  </div>
                  <div>
                    <Label className="text-slate-300">Válido Hasta</Label>
                    <Input 
                      type="date"
                      value={formData.premium_until}
                      onChange={function(e) { updateField('premium_until', e.target.value); }}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Galería de Imágenes */}
          {activeSection === 'images' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Image className="w-5 h-5 text-emerald-400" />
                  Galería de Imágenes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Images */}
                <div>
                  <Label className="text-slate-300 mb-3 block">Imágenes Actuales ({formData.gallery.length})</Label>
                  {formData.gallery.length > 0 ? (
                    <div className="grid grid-cols-4 gap-4">
                      {formData.gallery.map(function(img, index) {
                        return (
                          <div key={index} className="relative group">
                            <div className={'aspect-square rounded-lg overflow-hidden border-2 ' + 
                              (index === 0 ? 'border-emerald-500' : 'border-slate-700')}>
                              <img src={img} alt={'Imagen ' + (index + 1)} className="w-full h-full object-cover" />
                            </div>
                            {index === 0 && (
                              <span className="absolute top-2 left-2 px-2 py-1 bg-emerald-500 text-white text-xs rounded">
                                Principal
                              </span>
                            )}
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                              {index !== 0 && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={function() { setMainImage(index); }}
                                  className="bg-emerald-500 hover:bg-emerald-600"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={function() { removeGalleryImage(index); }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-800/50 rounded-lg border border-dashed border-slate-700">
                      <Image className="w-12 h-12 mx-auto text-slate-600 mb-2" />
                      <p className="text-slate-400">No hay imágenes en la galería</p>
                    </div>
                  )}
                </div>

                {/* Upload New */}
                <div>
                  <Label className="text-slate-300 mb-2 block">Agregar Nueva Imagen</Label>
                  <div className="flex items-center gap-4">
                    <label className="cursor-pointer flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={function(e) { handleImageUpload(e, false); }}
                        className="hidden"
                      />
                      <div className="flex items-center justify-center gap-2 p-4 bg-slate-800 hover:bg-slate-700 rounded-lg border border-dashed border-slate-600 transition-colors">
                        {uploading ? (
                          <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                        ) : (
                          <Plus className="w-5 h-5 text-emerald-400" />
                        )}
                        <span className="text-slate-300">Subir Imagen</span>
                      </div>
                    </label>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">JPG, PNG o WebP. Máximo 5MB.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Detalles del Taller */}
          {activeSection === 'details' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-400" />
                  Detalles del Taller
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Modalidad */}
                <div>
                  <Label className="text-slate-300 mb-3 block">Modalidad</Label>
                  <div className="flex gap-3">
                    {modalityOptions.map(function(mod) {
                      return (
                        <button
                          key={mod.value}
                          type="button"
                          onClick={function() { updateField('modality', mod.value); }}
                          className={'px-4 py-2 rounded-lg border transition-all ' +
                            (formData.modality === mod.value 
                              ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' 
                              : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600')}
                        >
                          {mod.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Duración</Label>
                    <Input 
                      value={formData.duration}
                      onChange={function(e) { updateField('duration', e.target.value); }}
                      placeholder="2 horas, 1 día, 4 semanas..."
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Capacidad</Label>
                    <Input 
                      type="number"
                      value={formData.capacity}
                      onChange={function(e) { updateField('capacity', e.target.value ? parseInt(e.target.value) : ''); }}
                      placeholder="Máximo de participantes"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Horario</Label>
                  <Input 
                    value={formData.schedule}
                    onChange={function(e) { updateField('schedule', e.target.value); }}
                    placeholder="Sábados 10:00-12:00, Martes y Jueves 18:00-20:00..."
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Requisitos para Participar</Label>
                  <Textarea 
                    value={formData.requirements}
                    onChange={function(e) { updateField('requirements', e.target.value); }}
                    placeholder="Describe los requisitos previos, materiales necesarios, etc."
                    className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Estado */}
          {activeSection === 'status' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Estado del Taller
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600">
                  <div>
                    <p className="text-white font-medium">Taller Público</p>
                    <p className="text-sm text-slate-400">El taller aparece en el directorio</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={function(e) { updateField('is_public', e.target.checked); }}
                    className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600">
                  <div>
                    <p className="text-white font-medium">Taller Verificado</p>
                    <p className="text-sm text-slate-400">Muestra el badge de verificación</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.is_verified}
                    onChange={function(e) { updateField('is_verified', e.target.checked); }}
                    className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                  />
                </label>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
